import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    ChevronDown,
    ImagePlus,
    Instagram,
    Loader2,
    MessageCircle,
    Phone,
    Store,
    Truck,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { INSTAGRAM_DM_URL, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/data/site";

const OCCASION_OPTIONS = [
    "Wedding",
    "Birthday",
    "Vintage cake",
    "Engagement",
    "Something else",
];

const FLAVOURS = [
    "Banana",
    "Chocolate",
    "Chocolate fudge",
    "Coffee",
    "Confetti",
    "Red velvet",
    "Ribbon",
    "Vanilla",
];

const WEIGHTS = [
    "Bento (300g)",
    "500g",
    "750g",
    "1kg",
    "1.5kg",
    "2kg",
    "Wedding cake structure",
];

const EMPTY = {
    name: "",
    phone: "",
    occasion: "",
    flavour: "",
    weight: "",
    details: "",
    date: "",
    mode: "delivery",
    address: "",
};

const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const MAX_PHOTOS = 5;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif"];

const isHeic = (file) =>
    ["image/heic", "image/heif"].includes(file.type) || /\.hei[cf]$/i.test(file.name);

const compressImage = async (file) => {
    let source = file;
    if (isHeic(file)) {
        const heic2any = (await import("heic2any")).default;
        const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        source = Array.isArray(converted) ? converted[0] : converted;
    }
    const bitmap = await createImageBitmap(source, { imageOrientation: "from-image" });
    const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    for (const q of [0.85, 0.7, 0.55]) {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", q));
        if (!blob) throw new Error("Could not read that photo");
        if (blob.size <= 900 * 1024 || q === 0.55) return blob;
    }
};

export default function OrderForm() {
    const [form, setForm] = useState(EMPTY);
    const [igOrder, setIgOrder] = useState(null);
    const [igCopied, setIgCopied] = useState(false);
    const [photos, setPhotos] = useState([]);
    const fileInputRef = useRef(null);
    const uploadingPhotos = photos.some((p) => p.status === "uploading");
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    useEffect(() => {
        const lenis = window.__lenis;
        if (igOrder) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [igOrder]);

    useEffect(() => {
        if (!igOrder) return;
        const onKey = (e) => {
            if (e.key === "Escape") setIgOrder(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [igOrder]);

    const today = new Date().toISOString().split("T")[0];

    const removePhoto = (id) => {
        setPhotos((ps) => {
            const found = ps.find((x) => x.id === id);
            if (found) URL.revokeObjectURL(found.previewUrl);
            return ps.filter((x) => x.id !== id);
        });
    };

    const addPhoto = async (file) => {
        if (file.size > MAX_PHOTO_SIZE) {
            toast.error(`"${file.name}" is larger than 10MB — please attach a smaller photo.`);
            return;
        }
        if (!ACCEPTED_PHOTO_TYPES.includes(file.type) && !isHeic(file)) {
            toast.error(`"${file.name}" isn't a JPG, PNG or HEIC photo.`);
            return;
        }
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const entry = {
            id,
            name: file.name,
            previewUrl: URL.createObjectURL(file),
            url: null,
            status: "uploading",
        };
        setPhotos((ps) => [...ps, entry]);
        try {
            const blob = (await compressImage(file).catch(() => null)) || file;
            const contentType = blob.type || "image/jpeg";
            const fd = new FormData();
            fd.append("file", blob, contentType === "image/png" ? "photo.png" : "photo.jpg");
            const res = await fetch(`${BACKEND_URL}/api/uploads`, {
                method: "POST",
                body: fd,
            });
            if (!res.ok) {
                let detail = "";
                try {
                    detail = (await res.text()).replace(/\s+/g, " ").slice(0, 140);
                } catch {}
                throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ""}`);
            }
            const data = await res.json();
            setPhotos((ps) =>
                ps.map((p) =>
                    p.id === id
                        ? { ...p, url: `${window.location.origin}/p/${data.code}`, status: "done" }
                        : p,
                ),
            );
        } catch (err) {
            setPhotos((ps) => ps.filter((p) => p.id !== id));
            URL.revokeObjectURL(entry.previewUrl);
            toast.error(
                `Couldn't upload "${file.name}" — ${err?.message || "something went wrong"}. Your order will send without it, or try again in a moment.`,
                { duration: 8000 },
            );
        }
    };

    const handlePhotosSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_PHOTOS - photos.length;
        if (room <= 0) {
            toast.error(`You can attach up to ${MAX_PHOTOS} photos.`);
            return;
        }
        if (files.length > room) {
            toast.error(`You can attach up to ${MAX_PHOTOS} photos — adding the first ${room}.`);
        }
        for (const file of files.slice(0, room)) {
            await addPhoto(file);
        }
    };

    const buildMessage = () => {
        const missing = [];
        if (!form.name.trim()) missing.push("your name");
        if (!form.phone.trim()) missing.push("your phone number");
        if (!form.occasion) missing.push("the occasion");
        if (!form.flavour) missing.push("the cake flavour");
        if (!form.weight) missing.push("the weight");
        if (!form.details.trim()) missing.push("the additional notes");
        if (!form.date) missing.push("the date needed");
        if (form.mode === "delivery" && !form.address.trim())
            missing.push("the delivery address");
        if (missing.length) {
            toast.error(`Please add ${missing.join(", ")}.`);
            return null;
        }
        const prettyDate = new Date(`${form.date}T00:00:00`).toLocaleDateString(
            "en-GB",
            { weekday: "long", day: "numeric", month: "long", year: "numeric" },
        );
        return [
            "Hi Cakes'alot! I'd like to order a cake.",
            "",
            `Name: ${form.name.trim()}`,
            `Phone: ${form.phone.trim()}`,
            `Occasion: ${form.occasion}`,
            `Flavour: ${form.flavour}`,
            `Weight: ${form.weight}`,
            `Additional notes: ${form.details.trim()}`,
            ...photos
                .filter((p) => p.status === "done" && p.url)
                .map((p) => `Inspiration photo: ${p.url}`),
            `Date needed: ${prettyDate}`,
            form.mode === "delivery"
                ? `Delivery — address: ${form.address.trim()}`
                : "Pickup — I'll collect the cake",
        ].join("\n");
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                return ok;
            } catch {
                return false;
            }
        }
    };

    const sendWhatsApp = () => {
        if (uploadingPhotos) return;
        const msg = buildMessage();
        if (!msg) return;
        window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`, "_blank");
    };

    const sendInstagram = async () => {
        if (uploadingPhotos) return;
        const msg = buildMessage();
        if (!msg) return;
        const copied = await copyText(msg);
        setIgCopied(copied);
        setIgOrder(msg);
    };

    const copyOrderAgain = async () => {
        const ok = await copyText(igOrder);
        if (ok) {
            setIgCopied(true);
            toast.success("Order copied!");
        } else {
            toast.error("Please long-press the text and copy it manually.");
        }
    };

    return (
        <section id="order" data-testid="order-section" className="py-20 sm:py-28">
            <div className="mx-auto max-w-2xl px-4 sm:px-6">
                <div className="text-center">
                    <p className="eyebrow">Ready when you are</p>
                    <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-cocoa sm:text-5xl">
                        order your cake
                    </h2>
                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-cocoa-soft sm:text-base">
                        Fill this in and send it your favourite way — we'll confirm
                        everything with you personally.
                    </p>
                </div>

                <div className="mt-10 rounded-[2rem] border border-blush/40 bg-white p-6 shadow-[0_30px_80px_-45px_rgba(44,34,30,0.35)] sm:p-10">
                    <div className="grid grid-cols-2 gap-3">
                        <a
                            data-testid="order-call-btn"
                            href={PHONE_TEL}
                            className="btn-outline !px-4 text-xs sm:text-sm"
                        >
                            <Phone className="size-4 shrink-0" />
                            Call {PHONE_DISPLAY}
                        </a>
                        <a
                            data-testid="order-message-btn"
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-outline !px-4 text-xs sm:text-sm"
                        >
                            <MessageCircle className="size-4 shrink-0" />
                            Message us
                        </a>
                    </div>

                    <div className="my-7 h-px bg-blush/30" />

                    <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="order-name" className="field-label">
                                    Your name
                                </label>
                                <input
                                    id="order-name"
                                    data-testid="order-name-input"
                                    className="field"
                                    type="text"
                                    placeholder="Amaya Perera"
                                    value={form.name}
                                    onChange={set("name")}
                                />
                            </div>
                            <div>
                                <label htmlFor="order-phone" className="field-label">
                                    Phone number
                                </label>
                                <input
                                    id="order-phone"
                                    data-testid="order-phone-input"
                                    className="field"
                                    type="tel"
                                    inputMode="tel"
                                    placeholder="077 123 4567"
                                    value={form.phone}
                                    onChange={set("phone")}
                                />
                            </div>
                        </div>

                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="order-occasion" className="field-label">
                                    Occasion
                                </label>
                                <div className="relative">
                                    <select
                                        id="order-occasion"
                                        data-testid="order-occasion-select"
                                        className="field appearance-none pr-10"
                                        value={form.occasion}
                                        onChange={set("occasion")}
                                    >
                                        <option value="">Choose one...</option>
                                        {OCCASION_OPTIONS.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cocoa-soft" />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="order-date" className="field-label">
                                    Date needed
                                </label>
                                <input
                                    id="order-date"
                                    data-testid="order-date-input"
                                    className="field"
                                    type="date"
                                    min={today}
                                    value={form.date}
                                    onChange={set("date")}
                                />
                            </div>
                        </div>

                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="order-flavour" className="field-label">
                                    Cake flavour
                                </label>
                                <div className="relative">
                                    <select
                                        id="order-flavour"
                                        data-testid="order-flavour-select"
                                        className="field appearance-none pr-10"
                                        value={form.flavour}
                                        onChange={set("flavour")}
                                    >
                                        <option value="">Choose a flavour...</option>
                                        {FLAVOURS.map((f) => (
                                            <option key={f} value={f}>
                                                {f}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cocoa-soft" />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="order-weight" className="field-label">
                                    Weight
                                </label>
                                <div className="relative">
                                    <select
                                        id="order-weight"
                                        data-testid="order-weight-select"
                                        className="field appearance-none pr-10"
                                        value={form.weight}
                                        onChange={set("weight")}
                                    >
                                        <option value="">Choose a weight...</option>
                                        {WEIGHTS.map((w) => (
                                            <option key={w} value={w}>
                                                {w}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cocoa-soft" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="order-details" className="field-label">
                                Additional notes
                            </label>
                            <textarea
                                id="order-details"
                                data-testid="order-details-input"
                                className="field min-h-32 resize-y"
                                placeholder="Message on the cake, colours, design ideas... Ordering more than one cake? List each one here with its flavour and weight."
                                value={form.details}
                                onChange={set("details")}
                            />
                        </div>

                        <div>
                            <span className="field-label">
                                Have a design in mind? Attach it here{" "}
                                <span className="font-medium normal-case tracking-normal text-cocoa-soft/70">
                                    (optional)
                                </span>
                            </span>
                            <input
                                ref={fileInputRef}
                                data-testid="order-photos-input"
                                type="file"
                                accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
                                multiple
                                className="hidden"
                                onChange={handlePhotosSelected}
                            />
                            <button
                                type="button"
                                data-testid="attach-photos-btn"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-blush/60 bg-cream/50 px-4 py-5 text-center text-xs font-semibold text-cocoa-soft transition-colors hover:border-leaf/50 hover:text-leaf sm:text-sm"
                            >
                                <ImagePlus className="size-5 shrink-0" />
                                Add inspiration photos — JPG, PNG or HEIC
                            </button>

                            {photos.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-4">
                                    {photos.map((p) => (
                                        <div
                                            key={p.id}
                                            data-testid="photo-preview"
                                            className="flex flex-col items-center"
                                        >
                                            <div className="relative">
                                                <img
                                                    src={p.previewUrl}
                                                    alt={p.name}
                                                    className="size-20 rounded-2xl object-cover ring-1 ring-blush/50"
                                                />
                                                {p.status === "uploading" && (
                                                    <div className="absolute inset-0 grid place-items-center rounded-2xl bg-cocoa/60 text-cream">
                                                        <Loader2 className="size-5 animate-spin" />
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid={`remove-photo-btn-${p.id}`}
                                                    onClick={() => removePhoto(p.id)}
                                                    aria-label={`Remove photo ${p.name}`}
                                                    className="absolute -right-2 -top-2 grid size-6 place-items-center rounded-full bg-cocoa text-cream shadow-md transition-transform hover:scale-110"
                                                >
                                                    <X className="size-3.5" />
                                                </button>
                                            </div>
                                            {p.status === "uploading" && (
                                                <span className="mt-1 text-[10px] font-semibold text-cocoa-soft">
                                                    Uploading photo...
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <span className="field-label">Delivery or pickup</span>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { key: "delivery", icon: Truck, label: "Delivery" },
                                    { key: "pickup", icon: Store, label: "Pickup" },
                                ].map((m) => (
                                    <button
                                        key={m.key}
                                        type="button"
                                        data-testid={`order-mode-${m.key}-btn`}
                                        onClick={() =>
                                            setForm((f) => ({ ...f, mode: m.key }))
                                        }
                                        className={`inline-flex h-12 items-center justify-center gap-2 rounded-2xl border text-sm font-semibold transition-all duration-200 ${
                                            form.mode === m.key
                                                ? "border-leaf bg-leaf text-cream shadow-md shadow-leaf/25"
                                                : "border-blush/50 bg-cream/60 text-cocoa-soft hover:border-leaf/40"
                                        }`}
                                    >
                                        <m.icon className="size-4" />
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <AnimatePresence initial={false}>
                            {form.mode === "delivery" && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <label
                                        htmlFor="order-address"
                                        className="field-label"
                                    >
                                        Delivery address
                                    </label>
                                    <input
                                        id="order-address"
                                        data-testid="order-address-input"
                                        className="field"
                                        type="text"
                                        placeholder="Street, town — e.g. Nugegoda, Colombo"
                                        value={form.address}
                                        onChange={set("address")}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="grid gap-3 pt-2 sm:grid-cols-2">
                            <button
                                type="button"
                                data-testid="send-whatsapp-btn"
                                onClick={sendWhatsApp}
                                disabled={uploadingPhotos}
                                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-leaf px-6 text-sm font-bold text-cream shadow-lg shadow-leaf/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-lg sm:text-base"
                            >
                                <MessageCircle className="size-5" />
                                Send via WhatsApp
                            </button>
                            <button
                                type="button"
                                data-testid="send-instagram-btn"
                                onClick={sendInstagram}
                                disabled={uploadingPhotos}
                                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-cocoa px-6 text-sm font-bold text-cream shadow-lg shadow-cocoa/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-lg sm:text-base"
                            >
                                <Instagram className="size-5" />
                                Send via Instagram
                            </button>
                        </div>

                        <p className="pt-1 text-center text-xs leading-relaxed text-cocoa-soft">
                            Your order is copied for you — paste it into our DMs and
                            hit send. No email needed — we're quickest on WhatsApp.
                        </p>
                    </form>
                </div>
            </div>

            <AnimatePresence>
                {igOrder && (
                    <motion.div
                        data-testid="instagram-panel"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setIgOrder(null)}
                        className="fixed inset-0 z-[110] flex items-end justify-center bg-cocoa/70 p-4 backdrop-blur-sm sm:items-center"
                    >
                        <motion.div
                            initial={{ y: 40, opacity: 0, scale: 0.98 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 40, opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="instagram-panel-card"
                            className="w-full max-w-md rounded-[2rem] bg-white p-6 text-center shadow-2xl sm:p-8"
                        >
                            <div className="mx-auto grid size-14 place-items-center rounded-full bg-blush-soft ring-1 ring-blush/60">
                                <Instagram className="size-6 text-cocoa" />
                            </div>
                            <h3
                                data-testid="instagram-panel-title"
                                className="mt-4 font-serif text-3xl font-semibold text-cocoa"
                            >
                                {igCopied ? "Order copied!" : "Copy your order"}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-cocoa-soft">
                                Open Instagram, paste your order into the chat and hit
                                send.
                            </p>

                            <div className="mt-5 rounded-2xl border border-gold/40 bg-gold-light/40 p-4 text-left">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-leaf">
                                        Your order
                                    </p>
                                    <button
                                        type="button"
                                        data-testid="instagram-copy-btn"
                                        onClick={copyOrderAgain}
                                        className="inline-flex h-8 shrink-0 items-center rounded-full border border-leaf/30 bg-white px-4 text-xs font-bold text-leaf transition-colors hover:bg-leaf hover:text-cream"
                                    >
                                        Copy
                                    </button>
                                </div>
                                <pre
                                    data-testid="instagram-order-text"
                                    className="mt-3 max-h-44 overflow-auto whitespace-pre-wrap text-left font-sans text-sm leading-relaxed text-cocoa"
                                >
                                    {igOrder}
                                </pre>
                            </div>

                            <a
                                data-testid="instagram-open-btn"
                                href={INSTAGRAM_DM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIgOrder(null)}
                                className="mt-6 inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-cocoa text-base font-bold text-cream shadow-lg shadow-cocoa/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0"
                            >
                                <Instagram className="size-5" />
                                Open Instagram
                            </a>

                            <button
                                type="button"
                                data-testid="instagram-panel-close-btn"
                                onClick={() => setIgOrder(null)}
                                className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-cocoa-soft transition-colors hover:text-cocoa"
                            >
                                Close
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
